import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Topbar from '../components/Topbar';
import Breadcrumb from '../components/Breadcrumb';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import '../styles/FacturacionGeneral.css';

const FacturacionGeneral = () => {
  const [filtros, setFiltros] = useState({
    fechaInicio: '',
    fechaFin: '',
    nombre: '',
    primer_apellido: '',
    segundo_apellido: '',
    nif: '',
    tratamiento: '',
    n_pieza: '',
    cuadrante: '',
    arcada: '',
    cantidad: '',
    importe: '',
    n_factura: '',
    clinica: ''
  });

  const [resultados, setResultados] = useState([]);
  const [mostrarGrafico, setMostrarGrafico] = useState(false);
  const calcularTotalFila = (cantidad, importe) => cantidad * importe;
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };
  
  const totalGlobal = resultados.reduce((acc, r) => {
    const cantidad = (1);
    const importe = Number(r.importe || 0);
    return acc + calcularTotalFila(cantidad, importe);
  }, 0);

  const handleBuscar = async () => {
    setLoading(true);
    try {
      let params = Object.fromEntries(
        Object.entries(filtros).filter(([_, v]) => v !== '' && v != null)
      );

      // Si no se indican fechas, usar hoy
      if (!params.fechaInicio && !params.fechaFin) {
        const hoy = new Date().toISOString().slice(0, 10);
        params.fechaInicio = hoy;
        params.fechaFin = hoy;
      }

      const res = await axios.get('/api/facturacion/general', {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setResultados(res.data);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    handleBuscar();
  }, []);
  const exportarExcel = () => {
    const datosExport = resultados.map(r => {
        const cantidad = Number(r.cantidad || 1);
        const importe = Number(r.importe || 0);
        const total = cantidad * importe;

        return {
        Día: r.dia ? new Date(r.dia).toLocaleDateString("es-ES") : "",
        Nombre: r.nombre,
        "Apellido 1": r.primer_apellido,
        "Apellido 2": r.segundo_apellido,
        NIF: r.nif,
        Tratamiento: r.tratamiento,
        Pieza: r.n_pieza,
        Cuadrante: r.cuadrante,
        Arcada: r.arcada,
        Cantidad: cantidad,
        Importe: `${importe.toFixed(2)} €`,
        Total: `${total.toFixed(2)} €`,
        "Nº Factura": r.n_factura,
        Clínica: r.clinica
        };
    });

    const hoja = XLSX.utils.json_to_sheet(datosExport);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, 'Facturación');

    const buffer = XLSX.write(libro, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: 'application/octet-stream' });

    saveAs(blob, 'facturacion.xlsx');
  };

  const datosGrafico = {
    labels: resultados.map(r => r.dia),
    datasets: [{
      label: 'Importe total por fechas',
      data: resultados.map(r => {
        const cantidad = Number(r.cantidad || 1);
        const importe = Number(r.importe || 0);
        return calcularTotalFila(cantidad, importe);
        }),
      backgroundColor: 'rgba(75, 192, 192, 0.6)'
    }]
  };

  return (
    <>
        <Topbar />
        <div className="facturacion-general">
        <Breadcrumb />
        <h2>📋 Datos de Facturación</h2>

        {/* Filtros */}
        <div className="filtros">
            <label>
                Fecha desde:
                <input
                type="date"
                name="fechaInicio"
                value={filtros.fechaInicio}
                onChange={handleChange}
                />
            </label>
            <label>
                Fecha hasta:
                <input
                type="date"
                name="fechaFin"
                value={filtros.fechaFin}
                onChange={handleChange}
                />
            </label>
            {Object.keys(filtros)
              .filter(campo => campo !== 'fechaInicio' && campo !== 'fechaFin')
              .map((campo) => (
                <input
                  key={campo}
                  name={campo}
                  placeholder={campo
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase())}
                  value={filtros[campo]}
                  onChange={handleChange}
                />
            ))}
            <button onClick={handleBuscar}>🔍 Buscar</button>
        </div>

        {/* Botones intermedios */}
        <div className="acciones">
            <button onClick={exportarExcel}>📤 Exportar a Excel</button>
            <button onClick={() => setMostrarGrafico(!mostrarGrafico)}>📊 Ver Gráfico</button>
        </div>

        {/* Tabla */}
        <table className="tabla-resultados">
            <thead>
            <tr>
                <th>Día</th><th>Nombre</th><th>Apellido 1</th><th>Apellido 2</th><th>NIF</th>
                <th>Tratamiento</th><th>Pieza</th><th>Cuadrante</th><th>Arco</th>
                <th>Cantidad</th><th>Importe</th><th>Total</th><th>Nº Factura</th><th>Clínica</th>
            </tr>
            </thead>
            <tbody>
            {resultados.map((r, i) => {
                const cantidad = Number(r.cantidad || 1);
                const importe = Number(r.importe || 0);
                const total = calcularTotalFila(cantidad, importe);
                
                return(
                    <tr key={i}>
                        <td>{r.dia ? new Date(r.dia).toLocaleDateString("es-ES") : ""}</td>
                        <td>{r.nombre}</td>
                        <td>{r.primer_apellido}</td>
                        <td>{r.segundo_apellido}</td>
                        <td>{r.nif}</td>
                        <td>{r.tratamiento}</td>
                        <td>{r.n_pieza}</td>
                        <td>{r.cuadrante}</td>
                        <td>{r.arcada}</td>
                        <td>{cantidad}</td>
                        <td>{importe.toFixed(2)} €</td>
                        <td>{total.toFixed(2)} €</td>
                        <td>{r.n_factura}</td>
                        <td>{r.clinica}</td>
                    </tr>
                );
            })}
            </tbody>
            <tfoot>
                <tr>
                    <td colSpan={11} style={{ textAlign: "right", fontWeight: "bold" }}>Total Global:</td>
                    <td style={{ fontWeight: "bold" }}>{totalGlobal.toFixed(2)} €</td>
                    <td colSpan={2}></td>
                </tr>
            </tfoot>
        </table>

        {/* Gráfico */}
        {mostrarGrafico && (
            <div className="grafico">
            <Bar data={datosGrafico} />
            </div>
        )}
        </div>
    </>        
  );
};

export default FacturacionGeneral;
